<template>
  <LoadingSpinner v-if="isLoading" />

  <div v-else class="edit-proposal">
    <ProjectHeader :project="project" />
    <ProjectSubNavigation :project="project" />

    <div class="mx-auto max-w-7xl px-4">
      <ProposalUpdateForm
        :project="project"
        :customer="customer"
        :budget="budget"
        :model="proposal"
        :change-orders="changeOrders"
        :auto-save="enableAutoSave"
        :auto-save-interval="autoSaveInterval"
        :save-function="saveProposal"
      />
    </div>
  </div>

</template>

<script lang="ts" setup>
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import { Proposal } from '@/models/proposal'
import { Budget } from '@/models/budget'
import ProposalUpdateForm from '../components/ProposalUpdateForm.vue'
import ProjectSubNavigation from '@/components/project/ProjectSubNavigation.vue'
import ProjectHeader from '@/components/project/ProjectHeader.vue'
import { useHead } from '@unhead/vue'
import { useNotification } from '@kyvg/vue3-notification'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { useRouter } from 'vue-router'
import { useRoute } from 'vue-router'
import BudgetService from '@/services/BudgetService'
import ProjectService from '@/services/ProjectService'
import CustomerService from '@/services/CustomerService'
import ProposalService from '@/services/ProposalService'
import { computed, ref, watch } from 'vue'
import SettingService from '@/services/SettingService'


useHead({ title: 'Edit proposal' })


const { notify } = useNotification()
const queryClient = useQueryClient()
const $router = useRouter()
const $route = useRoute()

const budgetService = new BudgetService()
const projectService = new ProjectService()
const customerService = new CustomerService()
const proposalService = new ProposalService()
const enableAutoSave = ref(true)
const autoSaveInterval = ref(10)

const proposalId = $route.params.id as string || ''

const { data: project, isLoading: projectLoading } = useQuery({
  queryKey: ['project', proposalId],
  queryFn: async () => projectService.find(proposalId),
})

const { data: budget, isLoading: budgetLoading } = useQuery({
  queryKey: ['budget', proposalId],
  queryFn: async () =>  {
    const budget = await budgetService.find(proposalId)
    const lineItems = await budgetService.getLineItems(proposalId)
    budget.lineItems = lineItems
    return budget
  },
})

const { data: proposal, isLoading: proposalLoading } = useQuery({
  queryKey: ['proposal', proposalId],
  queryFn: () => proposalService.find(proposalId),
})

const { data: changeOrders, isLoading: changeOrdersLoading } = useQuery({
  queryKey: ['changeOrders', proposalId],
  queryFn: () => proposalService.getChangeOrders(proposalId),
})

const { data:generalSettings, isLoading: contractTermsLoading } = useQuery({
  queryKey: ['settings', 'generalSettings'],
  queryFn: async () => await (new SettingService().find('generalSettings'))
})

watch(() => contractTermsLoading.value, (loading) => {
  if (!loading && generalSettings.value) {
    enableAutoSave.value = Object.hasOwn(generalSettings.value, 'enableProposalAutoSave') ? generalSettings.value.enableProposalAutoSave : true
    autoSaveInterval.value = generalSettings.value?.autoSaveWaitTime ?? 10
  }
}, { immediate: true })


const enableQuery = computed(() => proposal.value?.customerId !== '')

const { data: customer, isLoading: customerLoading } = useQuery({
  queryKey: ['customer', proposal.value?.customerId],
  queryFn: () => customerService.find(proposal.value?.customerId),
  enabled: enableQuery
})

const isLoading = computed(() => projectLoading.value || budgetLoading.value || proposalLoading.value || changeOrdersLoading.value || customerLoading.value || contractTermsLoading.value)

const { mutateAsync: saveProposal } = useMutation({
  mutationFn: async (payload: {proposal: Proposal, budget: Budget}) => {
    const updatedBudget = await budgetService.updateBudget(payload.budget, [])
    const updatedProposal = await proposalService.update(payload.proposal.projectId, payload.proposal)

    if (customer.value.id !== updatedProposal.customerId) {
      const updatedProject = await customerService.updateCustomerProject(customer.value.id, updatedProposal.projectId, { ...project.value, customerId: updatedProposal.customerId })
      queryClient.setQueryData(['project', updatedProject.id], updatedProject)
    }

    return { updatedBudget, updatedProposal }
  },
  onSuccess: ({ updatedBudget, updatedProposal }) => {
    queryClient.setQueryData(['budget', updatedBudget.id], updatedBudget)
    queryClient.setQueryData(['proposal', updatedProposal.id], updatedProposal)

    notify({
      type: 'success',
      title: 'Proposal updated',
      text: 'Proposal updated successfully'
    })
    $router.push({ name: 'ShowProposal', params: { id: project.value.id } })
  },
  onError: (error) => {
    notify({
      type: 'error',
      title: 'Error updating proposal',
      text: error.message || 'An error occurred while updating the proposal'
    })
  }
})

</script>
